import { Button, Card } from '@evw/ui';
import { useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { dateInTimeZone } from '../../lib/date';
import {
  createPublicHold,
  createPublicSession,
  getCatalogue,
  getPublicAvailability,
  type PublicSlot
} from './api';
import { Turnstile } from './Turnstile';

export function PublicBookingPage({ slug }: { slug: string }) {
  const catalogue = useQuery({
    queryKey: ['public-catalogue', slug],
    queryFn: () => getCatalogue(slug)
  });
  const [locationId, setLocationId] = useState('');
  const [serviceId, setServiceId] = useState('');
  const [staffId, setStaffId] = useState('');
  const [date, setDate] = useState('');
  const [slots, setSlots] = useState<PublicSlot[]>([]);
  const [slot, setSlot] = useState<PublicSlot | null>(null);
  const [givenName, setGivenName] = useState('');
  const [familyName, setFamilyName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [turnstileToken, setTurnstileToken] = useState<string>();
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  if (catalogue.isLoading)
    return (
      <main className="public-shell">
        <p>Loading booking options…</p>
      </main>
    );
  if (catalogue.error)
    return (
      <main className="public-shell">
        <p className="error">{catalogue.error.message}</p>
      </main>
    );
  const data = catalogue.data!;
  const service = data.services.find((item) => item.id === serviceId);
  const selectedDate =
    date ||
    dateInTimeZone(
      data.locations.find((item) => item.id === locationId)?.timeZone ?? data.business.timeZone
    );

  async function findTimes(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    setSlot(null);
    try {
      const result = await getPublicAvailability({
        slug,
        locationId,
        serviceId,
        ...(staffId ? { staffId } : {}),
        date: selectedDate
      });
      setSlots(result.slots.filter((item) => item.local_starts_at.startsWith(selectedDate)));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Unable to load availability');
    }
    setBusy(false);
  }

  async function hold(event: FormEvent) {
    event.preventDefault();
    if (!slot) return;
    setBusy(true);
    setError('');
    try {
      const session = await createPublicSession(slug);
      const result = await createPublicHold({
        slug,
        sessionToken: session.sessionToken,
        ...(turnstileToken ? { turnstileToken } : {}),
        slot,
        requestId: crypto.randomUUID(),
        givenName,
        familyName,
        email,
        phone
      });
      sessionStorage.setItem('evw-booking-token', result.managementToken);
      window.location.assign(`/manage#token=${encodeURIComponent(result.managementToken)}`);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Unable to reserve this time');
      setBusy(false);
    }
  }

  return (
    <main className="public-shell">
      <header className="public-header">
        <span className="eyebrow">Online booking</span>
        <h1>{data.business.name}</h1>
        <a href="/">Staff sign in</a>
      </header>
      <div className="booking-layout">
        <Card>
          <h2>Choose an appointment</h2>
          <form className="stack" onSubmit={(event) => void findTimes(event)}>
            <label>
              Location
              <select required value={locationId} onChange={(e) => setLocationId(e.target.value)}>
                <option value="">Choose a location</option>
                {data.locations.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Service
              <select required value={serviceId} onChange={(e) => setServiceId(e.target.value)}>
                <option value="">Choose a service</option>
                {data.services.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name} ·{' '}
                    {(item.grossAmountMinor / 100).toLocaleString(undefined, {
                      style: 'currency',
                      currency: item.currency
                    })}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Staff member
              <select value={staffId} onChange={(e) => setStaffId(e.target.value)}>
                <option value="">Any available</option>
                {data.staff.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.displayName}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Date
              <input
                required
                type="date"
                value={selectedDate}
                onChange={(e) => setDate(e.target.value)}
              />
            </label>
            <Button disabled={busy}>Find times</Button>
          </form>
          {error ? (
            <p className="error" role="alert">
              {error}
            </p>
          ) : null}
          <div className="slot-grid">
            {slots.map((item) => (
              <button
                type="button"
                className={
                  slot?.starts_at === item.starts_at && slot.staff_id === item.staff_id
                    ? 'slot selected'
                    : 'slot'
                }
                key={`${item.staff_id}-${item.starts_at}`}
                onClick={() => setSlot(item)}
              >
                {item.local_starts_at.slice(11, 16)}
                <small>
                  {data.staff.find((person) => person.id === item.staff_id)?.displayName}
                </small>
              </button>
            ))}
          </div>
        </Card>
        {slot ? (
          <Card>
            <h2>Your details</h2>
            <p>
              {service?.name} · {slot.local_starts_at.slice(0, 16).replace('T', ' ')}
            </p>
            <form className="stack" onSubmit={(event) => void hold(event)}>
              <label>
                First name
                <input
                  required
                  maxLength={80}
                  autoComplete="given-name"
                  value={givenName}
                  onChange={(e) => setGivenName(e.target.value)}
                />
              </label>
              <label>
                Last name
                <input
                  required
                  maxLength={80}
                  autoComplete="family-name"
                  value={familyName}
                  onChange={(e) => setFamilyName(e.target.value)}
                />
              </label>
              <label>
                Email
                <input
                  type="email"
                  maxLength={254}
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </label>
              <label>
                Phone
                <input
                  maxLength={40}
                  autoComplete="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </label>
              <p className="muted">Provide an email address or phone number.</p>
              <Turnstile onToken={setTurnstileToken} />
              <Button disabled={busy || (!email && !phone)}>
                Reserve for {data.policy.holdMinutes} minutes
              </Button>
            </form>
          </Card>
        ) : null}
      </div>
    </main>
  );
}
